/**
 * De onde vêm os números do Painel. A tela só conhece a interface `FonteDoPainel`; quem fala com o
 * banco é a implementação REST deste arquivo, e ela pode ser trocada pela camada `@studiolu/dados`
 * sem tocar em linha nenhuma da tela.
 *
 * CADA NÚMERO É O MESMO QUE A ÁREA MOSTRA, e a origem de cada um foi lida em `portal/admin.html`:
 *   Compromissos ... `compromissos_agenda.data_hora_inicio` (:4383), semana de domingo a sábado (:4345)
 *   Clientes ....... linhas de `perfis_cliente` (:3541), a mesma lista da aba Clientes
 *   Jornada ........ `jornada_cliente.etapa` (:3085 e :5631), uma linha por cliente e lead
 *   Vendas ......... `leads_funil.status` (:3059), as onze colunas do Kanban (:6582)
 *   Matriz ......... `leads_funil.produtos` e `status` por cliente (:3586-3588 e :3640-3660)
 *   Depoimentos .... `questionario_avaliativo` autorizado e com texto (:3673-3678)
 *   Financeiro ..... `parcelas` dos pagamentos de leads vinculados (:8140-8149), mês pelo vencimento (:8185)
 *   Arquivos ....... Storage `materiais/admin-arquivos` (:6418 e :6387)
 *   Sistema ........ linhas de `usuarios_sistema` (:8825)
 *
 * POR QUE `fetch` E NÃO O SDK: o lint do Chassi proíbe o cliente de dados do Supabase dentro de
 * `apps/` (ADR 0002). A leitura é HTTP puro, com a chave publicável e o token da sessão, e o RLS
 * decide o que volta, igual a `dados/acesso.ts`.
 *
 * POR QUE CONTAGEM POR `HEAD`: quando só o número importa, o banco devolve o total no cabeçalho
 * `Content-Range` e nenhuma linha de cliente viaja até o navegador. E a contagem é exata mesmo
 * acima do teto de 1000 linhas por resposta do PostgREST, que cortaria uma contagem feita no
 * navegador sem avisar ninguém.
 */

// URL do projeto e chave publicável: as mesmas do provedor de identidade e da leitura de acesso.
import { CHAVE_SUPABASE, URL_SUPABASE } from '../../identidade.js';

/* ===========================================================================
   O VOCABULÁRIO DAS ÁREAS, copiado do admin atual com a linha de origem
   =========================================================================== */

// Uma etapa: a chave gravada no banco e o rótulo que a tela escreve.
export interface Etapa {
  readonly chave: string;
  readonly rotulo: string;
}

// As onze etapas do funil, na ordem das colunas do Kanban e com os rótulos de `STATUS_LEAD`
// (`admin.html:3059-3071`). A ordem importa: é a leitura da esquerda para a direita do funil.
export const ETAPAS_DO_FUNIL: readonly Etapa[] = [
  { chave: 'novo', rotulo: 'Novo' },
  { chave: 'contatado', rotulo: 'Contato realizado' },
  { chave: 'diagnostico_agendado', rotulo: 'Diagnóstico agendado' },
  { chave: 'proposta_enviada', rotulo: 'Proposta enviada' },
  { chave: 'fechado', rotulo: 'Fechado' },
  { chave: 'com_juridico', rotulo: 'Com jurídico' },
  { chave: 'pagamento_a_receber', rotulo: 'Pagamento a receber' },
  { chave: 'pagamento_recebido', rotulo: 'Pagamento recebido' },
  { chave: 'em_consultoria', rotulo: 'Em consultoria' },
  { chave: 'finalizada', rotulo: 'Finalizada' },
  { chave: 'perdido', rotulo: 'Perdido' },
];

// As cinco etapas da jornada pós-venda, na ordem de `ORDEM_JORNADA` e com os rótulos de
// `JORNADA_ETAPA_LABEL` (`admin.html:3085-3092`).
export const ETAPAS_DA_JORNADA: readonly Etapa[] = [
  { chave: 'diagnostico_inicial', rotulo: 'Diagnóstico Inicial' },
  { chave: 'questionario', rotulo: 'Questionário Geral' },
  { chave: 'avaliando_material', rotulo: 'Avaliando Material' },
  { chave: 'em_atendimento', rotulo: 'Em Atendimento' },
  { chave: 'concluido', rotulo: 'Concluído' },
];

// Os oito serviços que são as colunas da Matriz: as chaves de `PRODUTOS_LEAD` (`admin.html:3076-3085`),
// que a Matriz percorre com `Object.keys(PRODUTOS_LEAD)` (`admin.html:3640`).
const SERVICOS_DA_MATRIZ: readonly string[] = [
  'eip',
  'analise_proporcao_3d',
  'coloracao_pessoal',
  'montagem_looks',
  'mala_viagem',
  'personal_shopper',
  'acompanhamento_anual',
  'projetos_sob_medida',
];

// A pasta fixa dos Repositório de Arquivos no bucket `materiais` (`admin.html:6418`).
const PASTA_ARQUIVOS_ADMIN = 'admin-arquivos';

/* ===========================================================================
   O CONTRATO QUE A TELA CONHECE
   =========================================================================== */

// Compromissos que começam hoje e nesta semana (domingo a sábado, como o calendário do admin atual).
export interface NumerosDeCompromissos {
  readonly hoje: number;
  readonly semana: number;
}

// As células da Matriz: serviço em andamento, serviço finalizado, e lacuna (serviço não contratado).
export interface NumerosDaMatriz {
  readonly emAndamento: number;
  readonly finalizados: number;
  readonly lacunas: number;
}

// Depoimentos autorizados pela cliente, e os que a Lu aprovou para uso.
export interface NumerosDeDepoimentos {
  readonly autorizados: number;
  readonly aprovados: number;
}

// As parcelas com vencimento no mês corrente, em reais: as já pagas e as que faltam receber.
export interface NumerosDoFinanceiro {
  readonly recebido: number;
  readonly aReceber: number;
}

// Quantidade por chave de etapa. Etapa sem ninguém aparece com 0, e nunca some.
export type ContagemPorEtapa = Readonly<Record<string, number>>;

/**
 * O que a tela pede. Cada método é UMA leitura independente: se uma falhar, as outras seguem, e
 * cada cartão mostra o próprio estado.
 */
export interface FonteDoPainel {
  compromissos(agora: Date): Promise<NumerosDeCompromissos>;
  clientes(): Promise<number>;
  jornada(): Promise<ContagemPorEtapa>;
  funil(): Promise<ContagemPorEtapa>;
  matriz(): Promise<NumerosDaMatriz>;
  depoimentos(): Promise<NumerosDeDepoimentos>;
  financeiro(agora: Date): Promise<NumerosDoFinanceiro>;
  arquivos(): Promise<number>;
  usuarios(): Promise<number>;
}

/**
 * A falha de uma leitura, já com a frase que a tela mostra.
 *
 * POR QUE A FRASE NASCE AQUI E NÃO NA TELA: é aqui que se sabe o que houve (sessão vencida, acesso
 * negado, banco fora, rede fora). A tela só escreve a frase, e código HTTP nunca chega a quem usa:
 * ele vai para o console, que é onde quem mantém o sistema procura.
 */
export class ErroDeLeitura extends Error {
  constructor(readonly frase: string) {
    // A mensagem do `Error` é a própria frase, para um `console.error` do erro dizer a mesma coisa.
    super(frase);
    this.name = 'ErroDeLeitura';
  }
}

/* ===========================================================================
   AS DATAS, no fuso de quem está olhando
   =========================================================================== */

// Meia-noite do dia, na hora local: "hoje" é o dia do relógio da pessoa, e não o de Greenwich.
export function inicioDoDia(data: Date): Date {
  const d = new Date(data);
  d.setHours(0, 0, 0, 0);
  return d;
}

// O domingo da semana, à meia-noite: a mesma conta de `inicioDoPeriodo` do admin atual (`admin.html:4345`).
export function inicioDaSemana(data: Date): Date {
  const d = inicioDoDia(data);
  // `getDay()` é 0 no domingo, então voltar esse número de dias cai sempre no domingo.
  d.setDate(d.getDate() - d.getDay());
  return d;
}

// Soma dias sem mexer na data original. `setDate` atravessa fim de mês e horário de verão sozinho.
function somarDias(data: Date, dias: number): Date {
  const d = new Date(data);
  d.setDate(d.getDate() + dias);
  return d;
}

// A data no formato da coluna `date` do banco (AAAA-MM-DD), montada pelos campos LOCAIS: com
// `toISOString` a virada do dia em Greenwich mudaria o mês às 21h de Brasília.
export function dataDoBanco(data: Date): string {
  const ano = data.getFullYear();
  // `getMonth` começa em 0, e o banco escreve janeiro como 01.
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}

// O primeiro e o último dia do mês da data, como o filtro de período da aba Financeiro recebe.
export function mesDoBanco(data: Date): { readonly de: string; readonly ate: string } {
  // Dia 1 do mês corrente.
  const primeiro = new Date(data.getFullYear(), data.getMonth(), 1);
  // O dia 0 do mês seguinte é o último dia deste, qualquer que seja o tamanho do mês.
  const ultimo = new Date(data.getFullYear(), data.getMonth() + 1, 0);
  return { de: dataDoBanco(primeiro), ate: dataDoBanco(ultimo) };
}

/* ===========================================================================
   A IMPLEMENTAÇÃO REST
   =========================================================================== */

// Um filtro do PostgREST: a coluna e a expressão (`eq.novo`, `gte.2026-09-01`, `not.is.null`).
type Filtro = readonly [coluna: string, expressao: string];

// Quanto esperar por uma leitura antes de desistir. Quinze segundos cobrem o celular em rede ruim
// sem deixar o cartão girando para sempre, que da cadeira de quem olha é o mesmo que travado.
const ESPERA_MAXIMA_MS = 15_000;

// Quantas linhas pedir por página. É o teto padrão do PostgREST no Supabase: pedir mais não traz mais.
const LINHAS_POR_PAGINA = 1000;

// Quantos ids cabem num filtro `in.(...)`. Cem uuids dão uns 3,7 mil caracteres de endereço, longe
// do limite de 8 mil que proxies costumam impor.
const IDS_POR_FILTRO = 100;

// Opções da fonte: tudo tem padrão, e a suíte ou a camada de dados podem trocar cada peça.
export interface OpcoesDaFonteRest {
  readonly url?: string;
  readonly chave?: string;
  readonly buscar?: typeof fetch;
}

/**
 * A fonte que lê a API REST do Supabase com o token da sessão.
 *
 * Nada aqui escreve: só `HEAD`, `GET` e a listagem do Storage, que é um `POST` de LEITURA (é assim
 * que a API do Storage recebe prefixo e paginação).
 */
export function criarFonteRest(token: string, opcoes: OpcoesDaFonteRest = {}): FonteDoPainel {
  // Endereço e chave: os do projeto, a não ser que alguém passe outros.
  const url = opcoes.url ?? URL_SUPABASE;
  const chave = opcoes.chave ?? CHAVE_SUPABASE;
  // O `fetch` do navegador, preso à janela: chamado solto, alguns navegadores recusam com "Illegal invocation".
  const buscar = opcoes.buscar ?? ((entrada, init) => fetch(entrada, init));

  // Os cabeçalhos de toda leitura. A chave identifica o projeto; quem autoriza é o token da sessão.
  const cabecalhosBase = {
    apikey: chave,
    Authorization: `Bearer ${token}`,
  };

  /**
   * Faz uma chamada e devolve a resposta só quando ela é um sucesso. Qualquer outra coisa vira um
   * `ErroDeLeitura` com a frase certa, e o detalhe técnico vai para o console.
   */
  async function pedir(recurso: string, endereco: string, init: RequestInit): Promise<Response> {
    // O relógio de desistência: aborta a chamada se o banco não responder a tempo.
    const controle = new AbortController();
    const relogio = setTimeout(() => controle.abort(), ESPERA_MAXIMA_MS);
    let resposta: Response;
    try {
      resposta = await buscar(endereco, { ...init, signal: controle.signal });
    } catch (erro) {
      // Abortado pelo relógio: o banco não respondeu a tempo.
      if (controle.signal.aborted) {
        console.error(`Painel: ${recurso} excedeu ${ESPERA_MAXIMA_MS} ms.`);
        throw new ErroDeLeitura('O banco demorou demais para responder a esta leitura.');
      }
      // Qualquer outra exceção do `fetch` é rede fora ou bloqueio antes de chegar ao servidor.
      console.error(`Painel: ${recurso} não chegou ao banco.`, erro);
      throw new ErroDeLeitura('Sem conexão com o banco agora. Confira a internet e abra o Painel de novo.');
    } finally {
      // Com resposta ou sem, o relógio para: senão ele abortaria uma leitura já terminada.
      clearTimeout(relogio);
    }
    // 2xx é sucesso. O 206 (resposta parcial) entra aqui, e é o que o PostgREST devolve por página.
    if (resposta.ok) return resposta;
    // O código vai para o console, e nunca para a tela.
    console.error(`Painel: ${recurso} respondeu ${resposta.status}.`);
    // Sessão vencida ou token recusado: a saída é entrar de novo, e não esperar o banco voltar.
    if (resposta.status === 401) {
      throw new ErroDeLeitura('A sessão expirou. Saia e entre de novo para ver este número.');
    }
    // Acesso negado à tabela: não é defeito do banco, é o papel que não alcança este dado.
    if (resposta.status === 403) {
      throw new ErroDeLeitura('O seu acesso não permite ler este número.');
    }
    // 404 numa LISTA é defeito de rota ou de tabela, e não ausência: lista vazia volta 200 com [].
    if (resposta.status === 404) {
      throw new ErroDeLeitura('O banco não reconheceu esta leitura. O defeito é do sistema, e não do seu acesso.');
    }
    // Todo o resto (5xx, limite de requisições, proxy): o banco não respondeu como devia.
    throw new ErroDeLeitura('O banco não respondeu a esta leitura. Tente abrir o Painel de novo em instantes.');
  }

  // Monta o endereço de uma tabela com as colunas e os filtros. `append` permite repetir a mesma
  // coluna (`data_hora_inicio=gte...&data_hora_inicio=lt...`), que é como o PostgREST faz intervalo.
  function enderecoDaTabela(tabela: string, colunas: string, filtros: readonly Filtro[], extra: readonly Filtro[] = []): string {
    const parametros = new URLSearchParams();
    parametros.append('select', colunas);
    for (const [coluna, expressao] of [...filtros, ...extra]) parametros.append(coluna, expressao);
    return `${url}/rest/v1/${tabela}?${parametros.toString()}`;
  }

  // Lê o total do cabeçalho `Content-Range`, no formato `0-24/42` ou `*/42`. Sem o total, não há número.
  function totalDoCabecalho(recurso: string, resposta: Response): number {
    const intervalo = resposta.headers.get('content-range') ?? '';
    // O total é o que vem depois da barra.
    const total = Number(intervalo.split('/')[1]);
    if (!Number.isInteger(total) || total < 0) {
      console.error(`Painel: ${recurso} veio sem contagem (Content-Range "${intervalo}").`);
      throw new ErroDeLeitura('O banco respondeu sem a contagem pedida.');
    }
    return total;
  }

  // Conta as linhas que o RLS deixa ver, sem trazer nenhuma: `HEAD` com `Prefer: count=exact`.
  async function contar(tabela: string, filtros: readonly Filtro[] = []): Promise<number> {
    const resposta = await pedir(tabela, enderecoDaTabela(tabela, '*', filtros), {
      method: 'HEAD',
      headers: { ...cabecalhosBase, Prefer: 'count=exact' },
    });
    return totalDoCabecalho(tabela, resposta);
  }

  /**
   * Traz TODAS as linhas de uma consulta, página por página, só com as colunas pedidas.
   *
   * POR QUE PAGINAR: o PostgREST corta a resposta em 1000 linhas sem erro nenhum, e uma conta feita
   * sobre a primeira página pareceria certa com dado de teste e erraria com dado real.
   */
  async function lerTodas<T>(tabela: string, colunas: string, filtros: readonly Filtro[] = []): Promise<T[]> {
    const linhas: T[] = [];
    // O total real, que a primeira página informa. Até lá, "não sei" é infinito.
    let total = Number.POSITIVE_INFINITY;
    while (linhas.length < total) {
      const pagina: readonly Filtro[] = [
        ['offset', String(linhas.length)],
        ['limit', String(LINHAS_POR_PAGINA)],
      ];
      const resposta = await pedir(tabela, enderecoDaTabela(tabela, colunas, filtros, pagina), {
        method: 'GET',
        headers: { ...cabecalhosBase, Accept: 'application/json', Prefer: 'count=exact' },
      });
      total = totalDoCabecalho(tabela, resposta);
      const lote = (await resposta.json()) as T[];
      // Página vazia antes do total é o banco mudando no meio da leitura: para aqui em vez de girar.
      if (lote.length === 0) break;
      linhas.push(...lote);
    }
    return linhas;
  }

  // Parte uma lista em pedaços, para o filtro `in.(...)` não passar do tamanho de endereço seguro.
  function emPedacos<T>(lista: readonly T[], tamanho: number): T[][] {
    const pedacos: T[][] = [];
    for (let i = 0; i < lista.length; i += tamanho) pedacos.push(lista.slice(i, i + tamanho));
    return pedacos;
  }

  // Lê todas as linhas cujo campo está numa lista de ids, pedaço por pedaço, e junta tudo.
  async function lerPorIds<T>(
    tabela: string,
    colunas: string,
    campo: string,
    ids: readonly (string | number)[],
    filtros: readonly Filtro[] = [],
  ): Promise<T[]> {
    const partes = await Promise.all(
      emPedacos(ids, IDS_POR_FILTRO).map((pedaco) =>
        lerTodas<T>(tabela, colunas, [[campo, `in.(${pedaco.join(',')})`], ...filtros]),
      ),
    );
    return partes.flat();
  }

  return {
    async compromissos(agora) {
      // Hoje: da meia-noite de hoje à meia-noite de amanhã, pelo mesmo campo que o calendário filtra.
      const hoje = inicioDoDia(agora);
      // Semana: do domingo à meia-noite até o domingo seguinte, como o calendário de 7 dias.
      const semana = inicioDaSemana(agora);
      const [nHoje, nSemana] = await Promise.all([
        contar('compromissos_agenda', [
          ['data_hora_inicio', `gte.${hoje.toISOString()}`],
          ['data_hora_inicio', `lt.${somarDias(hoje, 1).toISOString()}`],
        ]),
        contar('compromissos_agenda', [
          ['data_hora_inicio', `gte.${semana.toISOString()}`],
          ['data_hora_inicio', `lt.${somarDias(semana, 7).toISOString()}`],
        ]),
      ]);
      return { hoje: nHoje, semana: nSemana };
    },

    // Todas as linhas de `perfis_cliente`: é exatamente a lista que a aba Clientes desenha.
    clientes: () => contar('perfis_cliente'),

    async jornada() {
      // Uma contagem por etapa, em paralelo. Etapa sem ninguém volta 0, e continua no gráfico.
      const numeros = await Promise.all(
        ETAPAS_DA_JORNADA.map((e) => contar('jornada_cliente', [['etapa', `eq.${e.chave}`]])),
      );
      return Object.fromEntries(ETAPAS_DA_JORNADA.map((e, i) => [e.chave, numeros[i] ?? 0]));
    },

    async funil() {
      // Uma contagem por coluna do Kanban, em paralelo, sem trazer nome nem contato de lead.
      const numeros = await Promise.all(
        ETAPAS_DO_FUNIL.map((e) => contar('leads_funil', [['status', `eq.${e.chave}`]])),
      );
      return Object.fromEntries(ETAPAS_DO_FUNIL.map((e, i) => [e.chave, numeros[i] ?? 0]));
    },

    async matriz() {
      // As linhas da Matriz são as clientes de `perfis_cliente`; só o id viaja.
      const [perfis, leads] = await Promise.all([
        lerTodas<{ user_id: string }>('perfis_cliente', 'user_id'),
        // Os leads vinculados, só com o que a Matriz usa: a cliente, os serviços e a etapa.
        lerTodas<{ user_id_vinculado: string; produtos: string[] | null; status: string }>(
          'leads_funil',
          'user_id_vinculado,produtos,status',
          [['user_id_vinculado', 'not.is.null']],
        ),
      ]);
      // Os serviços de cada cliente, separados como o admin atual separa (`admin.html:3586-3588`).
      const porCliente = new Map<string, { contratados: Set<string>; finalizados: Set<string> }>();
      for (const lead of leads) {
        const grupo = porCliente.get(lead.user_id_vinculado) ?? { contratados: new Set(), finalizados: new Set() };
        porCliente.set(lead.user_id_vinculado, grupo);
        // Lead perdido não conta; finalizada vai para finalizados; o resto está em andamento.
        const alvo = lead.status === 'finalizada' ? grupo.finalizados : lead.status === 'perdido' ? null : grupo.contratados;
        if (alvo) for (const produto of lead.produtos ?? []) alvo.add(produto);
      }
      // Cada célula cliente por serviço: finalizado vence em andamento, como o ✓ da Matriz (`admin.html:3655-3657`).
      let emAndamento = 0;
      let finalizados = 0;
      let lacunas = 0;
      for (const { user_id } of perfis) {
        const grupo = porCliente.get(user_id);
        for (const servico of SERVICOS_DA_MATRIZ) {
          if (grupo?.finalizados.has(servico)) finalizados += 1;
          else if (grupo?.contratados.has(servico)) emAndamento += 1;
          else lacunas += 1;
        }
      }
      return { emAndamento, finalizados, lacunas };
    },

    async depoimentos() {
      // O mesmo recorte da aba: autorizado pela cliente E com texto escrito (`admin.html:3676-3677`).
      const autorizado: readonly Filtro[] = [
        ['autoriza_depoimento', 'eq.true'],
        ['depoimento_texto', 'not.is.null'],
      ];
      const [autorizados, aprovados] = await Promise.all([
        contar('questionario_avaliativo', autorizado),
        // Dentre esses, os que a Lu marcou como aprovados para uso (`admin.html:3697`).
        contar('questionario_avaliativo', [...autorizado, ['aprovado_para_uso', 'eq.true']]),
      ]);
      return { autorizados, aprovados };
    },

    async financeiro(agora) {
      // A aba Financeiro só soma pagamentos de leads com cliente vinculada (`admin.html:8140-8145`).
      const leads = await lerTodas<{ id: string | number }>('leads_funil', 'id', [['user_id_vinculado', 'not.is.null']]);
      if (leads.length === 0) return { recebido: 0, aReceber: 0 };
      // Os pagamentos desses leads: só o id, que é o que liga às parcelas.
      const pagamentos = await lerPorIds<{ id: string | number }>(
        'pagamentos',
        'id',
        'lead_id',
        leads.map((l) => l.id),
      );
      if (pagamentos.length === 0) return { recebido: 0, aReceber: 0 };
      // As parcelas com vencimento no mês corrente: o filtro de período da aba usa o vencimento (`admin.html:8185`).
      const { de, ate } = mesDoBanco(agora);
      const parcelas = await lerPorIds<{ valor: number | string | null; pago: boolean | null }>(
        'parcelas',
        'valor,pago',
        'pagamento_id',
        pagamentos.map((p) => p.id),
        [
          ['data_vencimento', `gte.${de}`],
          ['data_vencimento', `lte.${ate}`],
        ],
      );
      // A soma é feita em centavos inteiros: somar reais em ponto flutuante erra na segunda casa.
      let recebidoCentavos = 0;
      let aReceberCentavos = 0;
      for (const parcela of parcelas) {
        // `numeric` pode chegar como texto; valor ausente não soma nada.
        const centavos = Math.round(Number(parcela.valor ?? 0) * 100);
        // Pago soma em recebido, e o resto em a receber, como o resumo da aba (`admin.html:8257`).
        if (parcela.pago) recebidoCentavos += centavos;
        else aReceberCentavos += centavos;
      }
      return { recebido: recebidoCentavos / 100, aReceber: aReceberCentavos / 100 };
    },

    async arquivos() {
      // A listagem do Storage é paginada pelo corpo do pedido, e não pelo endereço.
      let total = 0;
      for (let deslocamento = 0; ; deslocamento += LINHAS_POR_PAGINA) {
        const resposta = await pedir('materiais/admin-arquivos', `${url}/storage/v1/object/list/materiais`, {
          method: 'POST',
          headers: { ...cabecalhosBase, 'Content-Type': 'application/json' },
          // Os mesmos parâmetros que `storage.from('materiais').list(caminho)` manda, com página maior.
          body: JSON.stringify({
            prefix: PASTA_ARQUIVOS_ADMIN,
            limit: LINHAS_POR_PAGINA,
            offset: deslocamento,
            sortBy: { column: 'name', order: 'asc' },
          }),
        });
        const itens = (await resposta.json()) as { name: string; id: string | null }[];
        // Subpasta vem com `id` nulo, e o marcador de pasta vazia não é arquivo que alguém enviou.
        total += itens.filter((i) => i.id !== null && i.name !== '.emptyFolderPlaceholder').length;
        // Página incompleta é a última.
        if (itens.length < LINHAS_POR_PAGINA) return total;
      }
    },

    // As contas do painel: a mesma tabela da sub-aba Usuários do Sistema (`admin.html:8825`).
    usuarios: () => contar('usuarios_sistema'),
  };
}
